import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Container,
  Col,
  Row,
  Button,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import MultiSelectDropDown from "components/DropDown/MultiselectDropDown";
import { getAdminReportingData } from "actions/reporting";
import ReactBsTable from "components/Tables/ReactBsTable";
import { getPublisherList } from "actions/publisher";
import { getProductTypeList } from "actions/productType";
import { getDealList } from "actions/deal";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import moment from "moment";
import DatePicker from "react-datepicker";
import { formatValues } from "components/Common/commonFunctions";
import { getProductTypeListByPublisherID } from "actions/productType";
import { exportCsv } from "actions/publisher";
import NotificationAlert from "react-notification-alert";
import "react-datepicker/dist/react-datepicker.css";
import { getDemandPartnerList } from "actions/reporting";

const columnConfig = [
  { key: "publisher", label: "Publisher" },
  { key: "date", label: "Date" },
  { key: "deal", label: "Deal" },
  { key: "demandPartner", label: "Demand Partner" },
  { key: "productType", label: "Product" },
  {
    key: "rawRequests",
    label: "Raw Requests",
    formatter: (value) => value?.toLocaleString(),
  },
  {
    key: "rawImpressions",
    label: "Raw Impressions",
    formatter: (value) => value?.toLocaleString(),
  },
  {
    key: "revenue",
    label: "Revenue",
    formatter: (value) => <span className="font-weight-bold">{value}</span>,
  },
];

const sortFieldMap = {
  date: "Date",
  publisher:"PublisherName",
  deal:"DealName",
  demandPartner:"DemandPartner",
  productType:"ProductTypeName",
  rawRequests: "RawRequests",
  rawImpressions: "RawImpressions",
  revenue: "GrossRevenue",
  requests: "AdRequests",
  impressions: "Impressions",
  fillRate: "TotalFillRate",
  grossRevenue: "GrossRevenue",
  erpm: "ERPM",
  netRevenue: "NetRevenue",
  Ecpm: "ECPM",
  publisherPayout: "PublisherPayout",
  pnl: "Pnl",
  pnlPercentage: "PnlPercentage",
  demandPartner: "DemandPartner",
  month: "Month",
  bcmFee: "BcmFeeAmount",
  channel: "Channel",
};

const allColumns = columnConfig.map((col) => ({
  text: col.label,
  dataField: col.key,
  formatter: col.formatter,
  sort: !["demandPartner", "fillRate", "netRevenue"].includes(col.key),
  sortCaret: (order) => {
    if (!order) {
      return (
        <span className="sort-caret">
          <i className="fa fa-sort" />
        </span>
      );
    }
    return order === "asc" ? (
      <span className="sort-caret">
        <i className="fa fa-sort-up" />
      </span>
    ) : (
      <span className="sort-caret">
        <i className="fa fa-sort-down" />
      </span>
    );
  },
}));

const formatterMap = {
  revenue: (value) => (
    <strong style={{ display: "block", textAlign: "right" }}>${value}</strong>
  ),
  pnlPercentage: (value) => (
    <span
      className={
        value > 0
          ? "text-success font-weight-bold"
          : "text-danger font-weight-bold"
      }
      style={{ display: "block", textAlign: "right" }}
    >
      {value}%
    </span>
  ),
  request: (value) => (
    <span
      className={
        value > 0
          ? "text-success font-weight-bold"
          : "text-danger font-weight-bold"
      }
      style={{ display: "block", textAlign: "right" }}
    >
      {value}%
    </span>
  ),
  pnl: (value) => {
    const numValue = parseFloat(value) || 0;
    return (
      <span
        className={
          numValue > 0
            ? "text-success font-weight-bold"
            : "text-danger font-weight-bold"
        }
        style={{ display: "block", textAlign: "right" }}
      >
        {numValue >= 0
          ? `$${numValue.toFixed(2)}`
          : `-$${Math.abs(numValue).toFixed(2)}`}
      </span>
    );
  },
  netRevenue: (value) => (
    <strong style={{ display: "block", textAlign: "right" }}>${value}</strong>
  ),
  publisherPayout: (value) => (
    <strong style={{ display: "block", textAlign: "right" }}>${value}</strong>
  ),
  grossRevenue: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>${value}</span>
  ),
  Ecpm: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  fillRate: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}%</span>
  ),
  impressions: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  requests: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  rawImpressions: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  rawRequests: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  bcmFee: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>${value}</span>
  ),
  erpm: (value) => (
    <span style={{ display: "block", textAlign: "right" }}>{value}</span>
  ),
  channel: (value) => (
    <span style={{ display: "block", textAlign: "left" }}>{value}</span>
  ),
};

const generateColumns = (data = [], viewType = "", reportType = "", demandPartnerIDs = []) => {
  if (!data.length) return [];

  // Check if META (ID: 3) is selected in demand partner filter OR viewType is PLACEMENT
  const showChannelColumn = demandPartnerIDs.includes(3) || viewType === "PLACEMENT";

  // Define the exact column order based on view type
  const getColumnOrder = () => {
    // Base columns in the exact order from the original component
    const baseColumns = [
      "month",
      "date",
      "publisher",
      "deal",
      "demandPartner",
      "adUnitCode",
      "productType",
      "rawRequests",
      "requests",
      "rawImpressions",
      "impressions",
      "fillRate",
      "grossRevenue",
      "erpm",
      "netRevenue",
      "Ecpm",
      "publisherPayout",
      "pnl",
      "pnlPercentage",
      "bcmFee",
      "rpm"
    ];

    // Define which columns to show for each view type (preserving order)
    const viewTypeColumns = {
      DATE: [
        "date",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",
      ],
      PUBLISHER: [
        "publisher",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",
      ],
      PRODUCT: [
        "productType",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",
      ],
      DEALNAME: [
        "deal",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",
      ],
      MONTH: [
        "month",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",
      ],
      PLACEMENT: [
        "channel",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",
      ],
      default: [
        "date",
        "publisher",
        "deal",
        "demandPartner",
        "productType",
        "rawRequests",
        "requests",
        "rawImpressions",
        "impressions",
        "fillRate",
        "grossRevenue",
        "erpm",
        "netRevenue",
        "Ecpm",
        "publisherPayout",
        "pnl",
        "pnlPercentage",

      ],
    };

    let columns = viewTypeColumns[viewType] || viewTypeColumns.default;

    // Add channel column after "deal" if META is selected (but not for PLACEMENT since it's already added)
    if (showChannelColumn && viewType !== "PLACEMENT") {
      const dealIndex = columns.indexOf("deal");
      if (dealIndex !== -1) {
        columns = [
          ...columns.slice(0, dealIndex + 1),
          "channel",
          ...columns.slice(dealIndex + 1),
        ];
      } else {
        const insertIndex = columns.indexOf("date") + 1 || 1;
        columns = [
          ...columns.slice(0, insertIndex),
          "channel",
          ...columns.slice(insertIndex),
        ];
      }
    }

    return columns;
  };

  const visibleKeys = getColumnOrder();

  const sortCaret = (order) => {
    if (!order) {
      return (
        <span className="sort-caret" style={{ marginLeft: "8px" }}>
          <i className="fa fa-sort" />
        </span>
      );
    }
    return order === "asc" ? (
      <span className="sort-caret" style={{ marginLeft: "8px" }}>
        <i className="fa fa-sort-up" />
      </span>
    ) : (
      <span className="sort-caret" style={{ marginLeft: "8px" }}>
        <i className="fa fa-sort-down" />
      </span>
    );
  };

  // Return columns in the exact order defined
  return visibleKeys
    .filter(key => data[0].hasOwnProperty(key))
    .map((key) => ({
      text: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase()),
      dataField: key,
      formatter: formatterMap[key] || undefined,
      sort: key === "channel" ? true : !["demandPartner", "fillRate", "netRevenue"].includes(key),
      sortCaret,
      headerStyle: {
        textAlign:
          key === "rawRequests" ||
          key === "rawImpressions" ||
          key === "revenue" ||
          key === "pnl" ||
          key === "requests" ||
          key === "impressions" ||
          key === "fillRate" ||
          key === "grossRevenue" ||
          key === "netRevenue" ||
          key === "Ecpm" ||
          key === "publisherPayout" ||
          key === "erpm" ||
          key === "pnlPercentage"
            ? "right"
            : "left",
        padding: "8px 12px",
        margin: "4px 0",
      },
    }));
};

const Reporting_Raw = () => {
  const [data, setData] = useState({
    data: [],
    columns: allColumns,
    totalRecords: 0,
    totalCounts: null,
  });
  const [columnsModal, setColumnsModal] = useState(false);
  const [showLoader, setshowLoader] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setpageSize] = useState(50);
  const visibleColumnsSnapshot = useRef({});
  const notificationAlertRef = useRef(null);
  const isMounted = useRef(true);
  const initialLoadDone = useRef(false);

  const [filters, setFilters] = useState({
    report_type: "RAW",
    deal_search: [],
    product_type: [],
    view_by_options: "",
    publisher: [],
    date_range: "",
    start_date: "",
    end_date: "",
    view_type: "",
    filterParams: [],
    demand_partner: [],
  });
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });

  const [dropDownData, setDropDownData] = useState({
    publisherData: [],
    productTypeData: [],
    dealData: [],
    viewType: [
      { label: "Date", value: "DATE" },
      { label: "Publisher", value: "PUBLISHER" },
      { label: "Product", value: "PRODUCT" },
      { label: "Deal", value: "DEALNAME" },
      { label: "Month", value: "MONTH" },
      { label: "Channel", value: "PLACEMENT" }
    ],
    demand_partner: [],
  });

  const [visibleColumns, setVisibleColumns] = useState({});

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    fetchPublishers();
    getProductTypeData();
    getDealData();
    getAdminReportingList();
  }, []);

  useEffect(() => {
    getProductTypeData(filters.publisher);
  }, [filters.publisher]);

  useEffect(() => {
    const filterParams = [];

    if (Array.isArray(filters?.publisher) && filters.publisher.length > 0) {
      filterParams.push({
        field: "PublisherID",
        condition: "in",
        filterValues: filters.publisher.map(String),
      });
    }

    if (Array.isArray(filters?.deal_search) && filters.deal_search.length > 0) {
      filterParams.push({
        field: "DealID",
        condition: "in",
        filterValues: filters.deal_search.map(String),
      });
    }

    if (Array.isArray(filters?.product_type) && filters.product_type.length > 0) {
      filterParams.push({
        field: "ProductTypeID",
        condition: "in",
        filterValues: filters.product_type.map(String),
      });
    }

    if (Array.isArray(filters?.demand_partner) && filters.demand_partner.length > 0) {
      filterParams.push({
        field: "DemandPartnerID",
        condition: "in",
        filterValues: filters.demand_partner.map(String),
      });
    }

    if (filters?.start_date && filters?.end_date) {
      filterParams.push({
        field: "Date",
        condition: "btw",
        filterValues: [filters?.start_date, filters?.end_date],
      });
    }

    const newFilterParams = filterParams.length > 0 ? JSON.stringify(filterParams) : "";

    setFilters((prev) => ({
      ...prev,
      filterParams: newFilterParams,
    }));
  }, [
    filters.publisher,
    filters.deal_search,
    filters.product_type,
    filters.start_date,
    filters.end_date,
    filters.demand_partner,
  ]);

  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
    }

    const timer = setTimeout(() => {
      if (isMounted.current) {
        getAdminReportingList();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    pageNum,
    filters.filterParams,
    filters.report_type,
    pageSize,
    filters.view_type,
    filters.demand_partner,
    sortConfig.sortField,
    sortConfig.sortOrder,
  ]);

  useEffect(() => {
    if (!data?.columns?.length) return;

    const visibilityMap = data.columns.reduce((acc, col) => {
      acc[col.dataField] = true;
      return acc;
    }, {});

    setVisibleColumns(visibilityMap);
  }, [data.columns]);

  const toggleColumnsModal = () => {
    if (!columnsModal) {
      visibleColumnsSnapshot.current = { ...visibleColumns };
    }
    setColumnsModal(!columnsModal);
  };

  const handleCloseModal = () => {
    setVisibleColumns(visibleColumnsSnapshot.current);
    setColumnsModal(false);
  };

  const handleColumnToggle = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSizePerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setpageSize(newSize);
    setPageNum(1);
  };

  const tableColumns = useMemo(() => {
    if (!data.columns.length) return [];

    const getViewTypeColumns = () => {
      switch (filters.view_type) {
        case "DATE":
          return data.columns.filter(
            (col) =>
              col.dataField === "date" ||
              !["publisher", "deal", "productType", "demandPartner"].includes(col.dataField),
          );
        case "PUBLISHER":
          return data.columns.filter(
            (col) =>
              col.dataField === "publisher" ||
              !["date", "deal", "productType", "demandPartner"].includes(col.dataField),
          );
        case "PRODUCT":
          return data.columns.filter(
            (col) =>
              col.dataField === "productType" ||
              !["date", "publisher", "deal", "demandPartner"].includes(col.dataField),
          );
        case "DEALNAME":
          return data.columns.filter(
            (col) =>
              col.dataField === "deal" ||
              !["date", "publisher", "productType", "demandPartner"].includes(col.dataField),
          );
        case "MONTH":
          return data.columns.filter(
            (col) =>
              col.dataField === "month" ||
              !["date", "publisher", "deal", "productType", "demandPartner"].includes(col.dataField),
          );
        case "PLACEMENT":
          return data.columns.filter(
            (col) =>
              col.dataField === "channel" ||
              !["date", "publisher", "deal", "productType", "month", "demandPartner", "adUnitCode"].includes(col.dataField),
          );
        default:
          return data.columns;
      }
    };

    const viewTypeFilteredColumns = getViewTypeColumns();
    return viewTypeFilteredColumns.filter(
      (col) => visibleColumns[col.dataField],
    );
  }, [visibleColumns, data, filters.view_type]);

  const isAnyFilterApplied = useCallback((filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return value && value.length > 0;
      }
      if (key === "report_type") return false;
      return value !== "" && value !== null && value !== undefined;
    });
  }, []);

  const handleChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;

        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return { ...prev, start_date: startDate };
        }

        return {
          ...prev,
          start_date: startDate,
          end_date: "",
        };
      }

      let newFilters = { ...prev, [key]: value };

      if (key === "publisher") {
        newFilters.deal_search = [];
        newFilters.product_type = [];
      } else if (key === "product_type") {
        newFilters.deal_search = [];
      }
      return newFilters;
    });

    if (key === "publisher") {
      getProductTypeData(value);
      getDealData([], value);
    }

    if (key === "product_type") {
      getDealData(value, filters.publisher);
    }
    setPageNum(1);
  };

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
  };

  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) return;

    setFilters({
      report_type: filters.report_type,
      deal_search: [],
      product_type: [],
      view_by_options: "",
      PublisherName: [],
      date_range: "",
      start_date: "",
      end_date: "",
      filterParams: [],
      view_type: "",
      demand_partner: [],
    });

    setSortConfig({ dataField: "", sortField: "", sortOrder: "" });
    setPageNum(1);
    getProductTypeData();
    getDealData();
  };

  const getProductTypeData = async (publisherIDs) => {
    try {
      let res;
      if (Array.isArray(publisherIDs) && publisherIDs.length > 0) {
        res = await getProductTypeListByPublisherID(publisherIDs);
      } else {
        res = await getProductTypeList(
          0,
          `[{"field":"Status","condition":"eq","filterValues":["ACTIVE"]}]`,
          "",
        );
      }

      if (!isMounted.current) return;

      let productTypeData = [];
      if (res?.code === 200) {
        const products = res?.data?.ProductType || res || [];
        productTypeData = products
          .filter((item) => item?.status === "ACTIVE")
          .map((item) => ({
            label: item?.name || "",
            value: item?.ID,
          }));
      }

      setDropDownData((prev) => ({
        ...prev,
        productTypeData: productTypeData,
      }));
    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  const getDealData = async (productTypeIDs, publisherIDs) => {
    try {
      const filters = [];
      if (Array.isArray(productTypeIDs) && productTypeIDs.length > 0) {
        filters.push({
          field: "ProductTypeID",
          condition: "in",
          filterValues: productTypeIDs.map(String),
        });
      }
      if (Array.isArray(publisherIDs) && publisherIDs.length > 0) {
        filters.push({
          field: "PublisherID",
          condition: "in",
          filterValues: publisherIDs.map(String),
        });
      }

      const res = await getDealList(
        0,
        filters.length > 0 ? JSON.stringify(filters) : "",
        "",
      );
      const res2 = await getDemandPartnerList();
      const demandPartnerData =
        res2?.code === 200
          ? (res2?.data?.demandPartner || []).map((item) => ({
              label: item?.name || "",
              value: item?.ID,
            }))
          : [];

      if (!isMounted.current) return;

      const dealData =
        res?.code === 200
          ? (res?.data?.Deal || []).map((item) => ({
              label: item?.name || "",
              value: item?.ID,
            }))
          : [];

      setDropDownData((prev) => ({
        ...prev,
        dealData: dealData,
        demand_partner: demandPartnerData,
      }));
    } catch (error) {
      console.error("Error fetching deals:", error);
    }
  };

  const fetchPublishers = async (productTypeIds = []) => {
    try {
      const publisherRes = await getPublisherList(0);

      if (!isMounted.current) return;

      const publisherData =
        publisherRes?.code === 200
          ? (publisherRes?.data?.Publisher || []).map((item) => ({
              label: item?.companyName || "",
              value: item?.ID,
            }))
          : [];

      setDropDownData((prev) => ({
        ...prev,
        publisherData: publisherData,
      }));
    } catch (error) {
      console.error("Error fetching publishers:", error);
    }
  };

  const formatReportingRaw = (data = []) => {
    const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
    const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length === 0) && 
                     (filters.view_type === "PLACEMENT" || filters.view_type === "" || !filters.view_type);
    const isPlacementView = filters.view_type === "PLACEMENT";

      return data.map((item) => ({
      month: formatMonth(item.month) || "-",
      date: item.date || "-",
      publisher: item.publisher?.companyName || "-",
      deal: item.deal?.dealName || "-",
        // Add channel column only if META is selected
      ...(showChannel && { channel: item.channel || "-" }),
      demandPartner:
        item.demandPartner?.name ||
        item.demandPartner?.label ||
        item.demandPartner ||
        "-",
      adUnitCode: Array.isArray(item.deal?.adUnitCodes)
        ? item.deal.adUnitCodes.join(", ")
        : item.deal?.adUnitCodes ||
          item.deal?.gamNetworkID ||
          item.deal?.adUnitCode ||
          "-",
      productType: item.productTypeID?.name || "-",
      rawRequests: formatValues(item.totalRequest) || 0,
      rawImpressions: formatValues(item.totalImpressions) || 0,
      rpm: formatValues((item.grossRevenue / item.totalImpressions) * 1000) || 0,
      fillRate: formatValues((item.totalImpressions / item.totalRequest) * 100) || 0,
      grossRevenue: formatValues(item.grossRevenue) || 0,
      bcmFee: formatValues(item.grossRevenue - item.grossRevenue * 0.9) || 0,
      netRevenue:
        item.demandPartner?.ID === 1
          ? formatValues(item.grossRevenue * 0.9)
          :  item.demandPartner?.ID === 2? 
          formatValues(item.grossRevenue * 0.95) || 0 :
          formatValues(item.grossRevenue),
    }));
  };


  function formatMonth(monthStr) {
    if (!monthStr || monthStr === "-") return "-";
    const [year, month] = monthStr.split("-");
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  }

  const getAdminReportingList = async () => {
    try {
      setshowLoader(true);

      const res = await getAdminReportingData(
        pageNum,
        filters,
        "",
        pageSize,
        filters.view_type,
        sortConfig.sortField,
        sortConfig.sortOrder,
      );

      if (!isMounted.current) return;

      if (res?.code === 200) {
        if (filters.report_type === "RAW") {
          const tableData = formatReportingRaw(res?.data?.ReportingRaw || []);
          const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
          const columns = generateColumns(
            tableData,
            filters.view_type,
            filters.report_type,
            demandPartnerIDs,
          );

          setData({
            data: tableData,
            columns: res?.data?.ReportingRaw?.length > 0 ? columns : columnConfig,
            totalRecords: res?.data?.totalRecords || 0,
            totalCounts: res?.data?.totalCounts || null,
          });
        } else {
          const tableData = flattenProcessedData(res?.data?.reportingProcessed || []);
          const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
          const columns = generateColumns(
            tableData,
            filters.view_type,
            filters.report_type,
            demandPartnerIDs,
          );

          setData({
            data: tableData,
            columns: res?.data?.reportingProcessed?.length > 0 ? columns : columnConfig,
            totalRecords: res?.data?.totalRecords || 0,
            totalCounts: res?.data?.totalCounts || null,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching reporting list:", error);
    } finally {
      if (isMounted.current) {
        setshowLoader(false);
      }
    }
  };

  const flattenProcessedData = (responseData = []) => {
    const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
    const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length === 0) && 
                     (filters.view_type === "PLACEMENT" || filters.view_type === "" || !filters.view_type);
    const isPlacementView = filters.view_type === "PLACEMENT";

    return responseData.map((item) => {
      const flattenedItem = {};

      // Only add context fields if not placement view (preserving original order)
      if (!isPlacementView) {
        flattenedItem.month = formatMonth(item.month) || "-";
        flattenedItem.date = item.date || "-";
        flattenedItem.publisher = item.publisher?.companyName || "-";
        flattenedItem.demandPartner = item.demandPartner?.name || item.demandPartner?.label || item.demandPartner || "-";
        flattenedItem.deal = item.deal?.dealName || "-";
        flattenedItem.productType = item.productType?.name || "-";
      }

      // Add channel column if showing channel
      if (showChannel) {
        flattenedItem.channel = item.channel || "-";
      }

      // Add metrics (always present)
      flattenedItem.rawRequests = formatValues(item.rawRequests) || 0;
      flattenedItem.requests = formatValues(item.adRequests) || 0;
      flattenedItem.rawImpressions = formatValues(item.rawImpressions) || 0;
      flattenedItem.impressions = formatValues(item.impressions) || 0;
      flattenedItem.fillRate = formatValues(item.fillRate) || "0%";
      flattenedItem.grossRevenue = formatValues(item.grossRevenue) || 0;
      flattenedItem.erpm = formatValues(item.eRPM) || 0;
      flattenedItem.netRevenue = formatValues(item.netRevenue) || 0;
      flattenedItem.Ecpm = formatValues(item.eCPM) || 0;
      flattenedItem.publisherPayout = formatValues(item.publisherPayout) || 0;
      flattenedItem.pnl = item.pnl || 0;
      flattenedItem.pnlPercentage = formatValues(item.pnlPercentage) || 0;
      
      return flattenedItem;
    });
  };

  const notify = (type, message) => {
    notificationAlertRef.current?.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const handleExportCsv = async () => {
    try {
      setshowLoader(true);
      const res = await exportCsv(pageNum, filters, "", pageSize, filters.view_type);
      if (res && isMounted.current) {
        const blob = new Blob(["\uFEFF" + res], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `report_${moment().format("YYYY-MM-DD")}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notify("success", "CSV exported successfully");
      } else {
        notify("danger", "No data received for export");
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
      notify("danger", "Something went wrong");
    } finally {
      if (isMounted.current) {
        setshowLoader(false);
      }
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const showCustomDateInputs = (name, value) => {
    const today = moment().format("YYYY-MM-DD");
    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        start_date: "",
        end_date: "",
      }));
      return;
    }
    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;
    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");
    setFilters((prev) => ({
      ...prev,
      date_range: value,
      start_date: startDate,
      end_date: today,
    }));
  };

  const tableData = useMemo(() => {
    if (!data?.data?.length) return [];
    if (!data?.totalCounts) return data.data;

    const demandPartnerIDs = filters.demand_partner.map(id => Number(id));
    const showChannel = (demandPartnerIDs.length === 1 || demandPartnerIDs.includes(3) || demandPartnerIDs.length === 0) && 
                     (filters.view_type === "PLACEMENT" || filters.view_type === "" || !filters.view_type);
    const isPlacementView = filters.view_type === "PLACEMENT";

    const totalRow = {
      srNo: "TOTAL_ROW",
    };

    // Only add context fields to total row if not placement view
    if (!isPlacementView) {
      totalRow.month = filters.view_type === "MONTH" ? "TOTAL" : "";
      totalRow.date =  filters.view_type === "DATE" ? "TOTAL" : "";
      totalRow.publisher = "TOTAL" ;
      totalRow.deal =  filters.view_type === "DEAL" ? "TOTAL" : "";
      // totalRow.demandPartner =  filters.view_type === "DEMAND_PARTNER" ? "TOTAL" : "";
      totalRow.productType =  filters.view_type === "PRODUCT" ? "TOTAL" : "";
    }

        if (showChannel && isPlacementView) {
      totalRow.channel = "TOTAL";
    }

    // Add metrics
    totalRow.rawRequests = formatValues(data.totalCounts.totalOfRawRequests) || 0;
    totalRow.requests = formatValues(data.totalCounts.totalOfRequests) || 0;
    totalRow.rawImpressions = formatValues(data.totalCounts.totalOfRawImpressions) || 0;
    totalRow.impressions = formatValues(data.totalCounts.totalOfImpressions) || 0;
    totalRow.fillRate = formatValues((data.totalCounts.totalOfImpressions / data.totalCounts.totalOfRequests) * 100) || 0;
    totalRow.grossRevenue = formatValues(data.totalCounts.totalOfGrossRevenue) || 0;
    totalRow.erpm = formatValues((data.totalCounts.totalOfNetRevenue / data.totalCounts.totalOfImpressions) * 1000) || 0;
    totalRow.netRevenue = formatValues(data.totalCounts.totalOfNetRevenue) || 0;
    totalRow.Ecpm = formatValues((data.totalCounts.totalOfPublisherPayout / data.totalCounts.totalOfImpressions) * 1000) || 0;
    totalRow.publisherPayout = formatValues(data.totalCounts.totalOfPublisherPayout) || 0;
    totalRow.pnl = data.totalCounts.totalOfPnl || 0;
    totalRow.pnlPercentage = formatValues((data.totalCounts.totalOfPnl / data.totalCounts.totalOfNetRevenue) * 100) || 0;
    totalRow.bcmFee = formatValues(data.totalCounts.totalOfGrossRevenue - data.totalCounts.totalOfNetRevenue) || 0;
    totalRow.rpm = formatValues((data.totalCounts.totalOfGrossRevenue / data.totalCounts.totalOfImpressions) * 1000) || 0;

    return [...data.data, totalRow];
  }, [data, filters.view_type, filters.demand_partner]);

  const filterSection = () => {
    return (
      <>
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>
        <Row className="align-items-center mb-2">
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.publisherData}
              placeholder="Select Publisher"
              value={filters.publisher}
              onChange={(e) => handleChange("publisher", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="publisher"
            />
          </Col>
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.productTypeData}
              placeholder="Product Type"
              value={filters.product_type}
              onChange={(e) => handleChange("product_type", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="product_type"
            />
          </Col>
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.dealData}
              placeholder="Select Deal"
              value={filters.deal_search}
              onChange={(e) => handleChange("deal_search", e.target.value)}
              isSearchable={true}
              isMulti={true}
              name="deal_search"
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              options={dropDownData.viewType}
              placeholder="Select View Type"
              value={filters.view_type}
              onChange={(e) => handleChange("view_type", e.target.value)}
              isSearchable={true}
            />
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col md={2}>
            <SearchableDropdown
              options={DATE_RANGE_OPTIONS}
              placeholder="Date Range"
              value={filters.date_range}
              onChange={(e) => showCustomDateInputs("date_range", e.target.value)}
              isSearchable={false}
            />
          </Col>
          <Col md={2}>
            <div className="datepicker-wrapper">
              <DatePicker
                selected={filters.start_date ? new Date(filters.start_date) : null}
                onChange={(date) => handleChange("start_date", date ? formatDate(date) : "")}
                dateFormat="dd MMM yyyy"
                placeholderText="Start Date"
                className="form-control custom-datepicker"
                disabled={filters.date_range !== "Custom Range"}
              />
            </div>
          </Col>
          <Col md={2}>
            <div className="datepicker-wrapper">
              <DatePicker
                selected={filters.end_date ? new Date(filters.end_date) : null}
                onChange={(date) => handleChange("end_date", date ? formatDate(date) : "")}
                dateFormat="dd MMM yyyy"
                placeholderText="End Date"
                className="form-control custom-datepicker"
                disabled={filters.date_range !== "Custom Range" || !filters.start_date}
                minDate={filters.start_date ? new Date(filters.start_date) : undefined}
              />
            </div>
          </Col>
          <Col md={2}>
            <MultiSelectDropDown
              options={dropDownData.demand_partner}
              placeholder="Select Demand Partner"
              value={filters.demand_partner}
              onChange={(e) => handleChange("demand_partner", e.target.value)}
              isSearchable={true}
              isMulti={true}
            />
          </Col>
          <Col md={2}>
            <Button
              className="secondary"
              onClick={handleReset}
              disabled={!isAnyFilterApplied(filters)}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Container fluid className="mt--6">
      <Card>
        {tableColumns?.length > 0 && (
          <ReactBsTable
            title="Admin Performance Report"
            data={tableData}
            columns={tableColumns}
            keyField="srNo"
            showAddbtn={false}
            handleSizePerPageChange={handleSizePerPageChange}
            renderFilters={filterSection}
            showColumnsBtn={true}
            Columnstext="Columns"
            toggleColumnsModal={toggleColumnsModal}
            exportCSVBtnShow={filters.report_type === "Processed"}
            ExportCSVtext="Export CSV"
            onexportCsvBtnClick={handleExportCsv}
            page={pageNum}
            enablePageSizeManagement={true}
            sizePerPage={pageSize}
            totalRecords={data?.totalRecords}
            onPageChange={(p) => setPageNum(p)}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
        )}
      </Card>

      <Modal isOpen={columnsModal} toggle={toggleColumnsModal}>
        <ModalHeader toggle={toggleColumnsModal}>Select Columns</ModalHeader>
        <ModalBody>
          {data?.columns.map((col) => (
            <label
              key={col.dataField}
              className="custom-checkbox-icon d-flex align-items-center mb-2"
            >
              <input
                type="checkbox"
                checked={visibleColumns[col.dataField]}
                onChange={() => handleColumnToggle(col.dataField)}
                hidden
              />
              <span className="checkbox-icon mr-2">
                {visibleColumns[col.dataField] && <i className="fa fa-check" />}
              </span>
              {col.text}
            </label>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button className="btn-gradient-primary" onClick={toggleColumnsModal}>
            Save Selection
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Reporting_Raw;